import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

const GATED_ACTIONS: { key: string; label: string; helper: string }[] = [
  {
    key: "campaign.send",
    label: "Campaign sends",
    helper:
      "A campaign cannot send until someone else approves it. Approval binds to the exact content, editing afterwards re-requires it.",
  },
  {
    key: "invoice.void",
    label: "Voiding invoices",
    helper: "An invoice cannot be voided without an approved request.",
  },
  {
    key: "invoice.refund",
    label: "Refunds",
    helper: "Marking an invoice refunded requires an approved request.",
  },
];

/**
 * Which actions need a second pair of eyes, per workspace, off by default.
 * A two-person shop can leave everything off; a bigger shop gates finance.
 * Owners, administrators and managers hold the decide permission; nobody can
 * approve their own request.
 */
export const ApprovalsSettings = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const workspaceId = getActiveWorkspaceId();

  const { data: enabled } = useQuery<string[]>({
    queryKey: ["approval-settings", workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("workspaces")
        .select("approval_required_for")
        .eq("id", workspaceId!)
        .single();
      if (error) throw new Error(error.message);
      return (data.approval_required_for as string[] | null) ?? [];
    },
  });

  const toggle = async (key: string) => {
    const current = enabled ?? [];
    const next = current.includes(key)
      ? current.filter((item) => item !== key)
      : [...current, key];
    const { error } = await getSupabaseClient()
      .from("workspaces")
      .update({ approval_required_for: next })
      .eq("id", workspaceId!);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["approval-settings"] });
    queryClient.invalidateQueries({ queryKey: ["approval-required"] });
  };

  return (
    <div className="flex flex-col gap-3">
      {GATED_ACTIONS.map((action) => (
        <label key={action.key} className="flex items-start gap-2 text-[13px]">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={(enabled ?? []).includes(action.key)}
            onChange={() => toggle(action.key)}
          />
          <span>
            <span className="font-medium">{action.label}</span>
            <span className="block text-[12px] text-muted-foreground">
              {action.helper}
            </span>
          </span>
        </label>
      ))}
      <p className="text-[11.5px] text-muted-foreground">
        Owners, administrators and managers can approve. Nobody can approve
        their own request.
      </p>
    </div>
  );
};
